/**
 * Anonymous identity for guests (no-login users).
 *
 * The paywall and other rate-limited endpoints need a stable identifier
 * for guests so we can enforce per-actor limits before they sign in.
 * We mint a UUID once per browser, persist it in localStorage, and send
 * it on every request as the ``X-Anon-Id`` header. After sign-in the
 * backend keys off ``user_id`` instead, so this value becomes irrelevant
 * automatically.
 *
 * Falls back to a sessionStorage-only id if localStorage is unavailable
 * (private mode), and finally to an in-memory id if both fail.
 */
const KEY = "o1:anon-id";

let _memoryId = null;

const newId = () => {
  // Prefer the platform UUID. Fall back to a quick random for very old
  // browsers / SSR.
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    /* noop */
  }
  return "anon-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
};

export const getAnonId = () => {
  // 1. Memory cache (fast path, also covers private-mode browsers)
  if (_memoryId) return _memoryId;

  // 2. localStorage (preferred — survives across tabs and sessions)
  try {
    const existing = window.localStorage.getItem(KEY);
    if (existing) {
      _memoryId = existing;
      return existing;
    }
    const id = newId();
    window.localStorage.setItem(KEY, id);
    _memoryId = id;
    return id;
  } catch {
    /* localStorage blocked — fall through */
  }

  // 3. sessionStorage fallback
  try {
    const existing = window.sessionStorage.getItem(KEY);
    if (existing) {
      _memoryId = existing;
      return existing;
    }
    const id = newId();
    window.sessionStorage.setItem(KEY, id);
    _memoryId = id;
    return id;
  } catch {
    /* fall through */
  }

  // 4. In-memory only (last resort)
  _memoryId = newId();
  return _memoryId;
};
